<template>
  <layout class="booking-table">
    <page-header :actions="actions">
      <template slot="header">
        <h4 class="mr-4">
          <i class="icon-stack mr-2"></i>
          {{ $t('bookings.title') }}
        </h4>
        <b-nav pills class="mb-0 booking-status">
          <b-nav-item
            v-for="(tab, key) in tabsVisible"
            :key="key"
            :active="key === currentTab"
            link-classes="py-1"
            :class="key"
            @click="setTab(key)"
          >
            {{ tab.label }}
            <b-badge v-if="tab.count !== null" variant="light">
              {{ tab.count }}
            </b-badge>
          </b-nav-item>
        </b-nav>
      </template>
    </page-header>
    <div class="content">
      <AdminTable
        v-if="currentDivision"
        ref="table"
        v-bind="tableProps"
        @api-result="apiResult"
      >
        <template slot="route" slot-scope="{ item }">
          <div>
            <strong v-if="item.is_shared">{{
              $t('bookings.shared_transfer')
            }}</strong>
            <strong v-else>{{
              $t('enums.ride_type.' + item.ride_type)
            }}</strong>
          </div>
          <booking-route :booking="item" :show-full-address="showFullAddress" />
        </template>
        <template slot="payment" slot-scope="{ item }">
          <b-badge variant="info" class="mr-2">
            {{ getOrder(item).status_label }}
          </b-badge>
          <value-payment-method :item="getOrder(item)" class="block" />
          <div
            v-if="
              getOrder(item).last_payment &&
              getOrder(item).payment_method === 'card'
            "
            class="text-muted text-xs"
          >
            <span
              v-if="getOrder(item).last_payment"
              v-b-tooltip.click
              :title="getOrder(item).last_payment.id"
              class="cursor-pointer whitespace-no-wrap"
            >
              Tid: {{ item.order.last_payment.id | truncate(16) }}
            </span>
          </div>
        </template>
        <template slot="vehicle" slot-scope="{ item }">
          <img
            v-if="item.vehicle.image && item.vehicle.image.url"
            :src="item.vehicle.image.url"
            class="w-32 max-w-full mr-3"
          />
          <div class="text-xs">{{ item.vehicle.name }}</div>
          <booking-amount :booking="item" />
        </template>
      </AdminTable>
    </div>
    <export ref="export" />

    <ActionModal
      id="block-modal"
      :loading="modalBusy"
      :title="$t('bookings.block')"
      custom-reason
      @confirm="onConfirmBlock"
    />
    <CompanyPenaltyModal
      id="company-penalty-modal"
      :busy="modalBusy"
      :title="$t('bookings.company_penalty')"
      :currency="activeItem && activeItem.order.currency"
      @ok="onConfirmCompanyPenalty"
    />

    <BookingFormModal
      ref="bookingModal"
      @saved="onBookingSave"
      @deleted="onBookingSave"
    />
  </layout>
</template>

<script>
import { create } from 'vue-modal-dialogs'

import ActionModal from './ActionModal.vue'
import AdminTable from '../components/AdminTable.vue'
import BookingsApi from './Api'
import Assigning from './Assigning'
import Export from './Export'
import BookingsIndexMixin from './BookingsIndexMixin'
import BookingRoute from './BookingRoute'
import CompanyPenaltyModal from './CompanyPenaltyModal.vue'

const resourceBase = 'bookings'

export default {
  name: 'Bookings',

  components: {
    CompanyPenaltyModal,
    ActionModal,
    AdminTable,
    BookingFormModal: () => import('./BookingFormModal.vue'),
    BookingRoute,
    Export,
  },

  mixins: [BookingsIndexMixin],

  data() {
    return {
      // Table resource configuration
      tableProps: {
        base: resourceBase,
        apiBuilder: () => {
          return new BookingsApi({
            context: 'list',
            division_id: this.currentDivision.id,
            ride_type: this.tabs[this.currentTab].ride_type,
            include:
              'vehicle,vehicle.media,returnBooking,order,returnBooking.order,origin,destination',
            with: 'totals',
            'where[is_return]': false,
            'where[status]': this.tabs[this.currentTab].status,
            ...this.tabs[this.currentTab].params,
          })
        },
        sortBy: 'ordered_at',
        sortDesc: false,
        perPage: 30,
        columns: [
          {
            key: 'number',
            label: this.$t('bookings.number'),
            sortable: true,
          },
          {
            key: 'ordered_at',
            label: this.$t('bookings.pickup_date'),
            sortable: true,
            component: 'value-date',
            config: {
              stacked: true,
              zone: true,
              zoneAttribute: 'timezone',
            },
            tdClass: (value, key, item) => {
              return this.orderDateVariant(item)
            },
          },
          {
            key: 'route',
            label: this.$t('bookings.route'),
            tdClass: 'text-sm',
          },
          {
            key: 'payment',
            label: this.$t('general.payment'),
          },
          {
            key: 'vehicle',
            label: this.$t('bookings.vehicle_class'),
          },
        ],

        searchFields: [
          {
            component: 'text-field',
            type: 'search',
            attribute: 'query',
            placeholder: this.$t('general.search'),
          },
          {
            component: 'date-range-field',
            attribute: 'where.ordered_at',
            placeholder: this.$t('general.select_period'),
          },
        ],

        searchData: {
          query: null,
          where: {
            number: null,
            ordered_at: null,
          },
        },

        rowActions: (item) => {
          let actions = []
          actions.push('edit')

          if (this.$allows(`${resourceBase}.update`)) {
            if (['pending', 'assigned', 'rejected'].indexOf(item.status) >= 0) {
              actions.push({
                label: this.$t('bookings.assign'),
                handler: () => {
                  this.assign(item)
                },
              })
            }
          }
          actions.push(...this.rowActions(item))

          return actions
        },

        bulkActions() {
          return []
        },

        onRowClick: (item) => {
          this.$refs.bookingModal.show(item.id)
        },
      },

      currentTab: null,

      tabs: {
        pending: {
          label: this.$t('enums.booking_status.pending'),
          status: 'pending',
          count: null,
        },
        assigned: {
          label: this.$t('enums.booking_status.assigned'),
          status: 'assigned',
          count: null,
        },
        rejected: {
          label: this.$t('enums.booking_status.rejected'),
          status: 'rejected',
          count: null,
        },
        cancelled: {
          label: this.$t('enums.booking_status.cancelled'),
          status: 'cancelled',
          count: null,
        },
        draft: {
          label: this.$t('enums.booking_status.unpaid'),
          status: 'draft',
          count: null,
        },
        city_taxi: {
          label: this.$t('enums.ride_type.city_taxi'),
          status: 'pending',
          ride_type: 'city_taxi',
          count: null,
        },
        delivery: {
          label: this.$t('enums.ride_type.delivery'),
          status: 'pending',
          ride_type: 'delivery',
          count: null,
        },
        requested_quote: {
          label: this.$t('bookings.requested_quote'),
          status: 'draft',
          params: { requested_quote: true },
          count: null,
        },
        shared_taxi: {
          label: this.$t('bookings.shared_transfer'),
          status: 'pending',
          params: { shared_taxi: true },
          count: null,
        },
        blocked: {
          label: this.$t('bookings.blocked'),
          count: null,
          params: {
            'where[is_blocked]': true,
            'where[is_return]': null,
          },
        },
      },

      activeItem: null,
      modalBusy: false,
    }
  },

  computed: {
    tabsVisible() {
      const tabs = this.tabs

      return this.$allows('bookings.index')
        ? tabs
        : {
            assigned: tabs.assigned,
            cancelled: tabs.cancelled,
            requested_quote: tabs.requested_quote,
          }
    },
    // Page header actions
    actions() {
      const actions = []

      if (this.$allows(`${resourceBase}.index`)) {
        actions.push({
          id: 'export',
          text: this.$t('bookings.export'),
          icon: 'icon-file-download',
          onClick: () => {
            this.$bvModal.show('export-dialog')
          },
        })
      }

      if (this.$allows(`${resourceBase}.create`)) {
        actions.push({
          id: 'create',
          text: this.$t('general.add_new'),
          icon: 'icon-plus-circle2',
          onClick: () => {
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.$router.push({
              name: `${resourceBase}.create`,
            })
          },
        })
      }

      return actions
    },
    showFullAddress() {
      return this.$allows('administer')
    },
  },

  watch: {
    currentDivision() {
      this.$refs.table && this.$refs.table.reset()
    },
  },

  created() {
    this.assignDialog = create(Assigning, 'booking')

    this.currentTab = Object.keys(this.tabsVisible)[0]
  },

  methods: {
    apiResult(result) {
      Object.keys(this.tabs).forEach((tab) => {
        this.tabs[tab].count = 0
        if (result.totals && result.totals[tab]) {
          this.tabs[tab].count = result.totals[tab]
        }
      })
    },

    setTab(key) {
      this.currentTab = key
      this.$refs.table.reset()
    },

    assign(booking) {
      this.assignDialog(booking).then((assigned) => {
        // Empty argument will mean that nothing is changed
        if (!assigned) {
          return
        }

        this.$refs.table.refresh()
      })
    },

    onBookingSave() {
      this.$refs.table.refresh()
    },
  },
}
</script>
